/* Dev-time console.warn wrappers.
 *
 * DCE design: consumer bundlers (Vite, webpack, Rollup, tsup) match
 * `process.env.NODE_ENV` as a literal AST pattern and substitute
 * "production" at build time, letting them strip the guarded body.
 * Optional chaining defeats that (`process.env?.NODE_ENV` is a
 * different MemberExpression shape and isn't substituted by the
 * default configs), so this file uses the plain form. The
 * `typeof process` guard keeps it safe in environments where
 * `process` is absent (browser without a define shim).
 *
 * The helpers are also invoked behind call-site `if` guards where
 * the cost of the string literal + function call matters (e.g.
 * render paths on hot primitives). For one-shot call sites the
 * function-form is fine — the body is still dead in prod, the
 * call itself is cheap.
 */

/*
 * IMPORTANT — this file is the SOLE legitimate `process` consumer
 * in the kit (browser-ESM contract, audit Finding #11). Every
 * access below is behind a `typeof process` guard and is computed
 * at module load, so consumer bundlers can substitute
 * `process.env.NODE_ENV` and DCE the guarded branch. No other
 * primitive should reference `process` directly — under browser-
 * ESM (CDN / import-maps) without a process shim, an unguarded
 * `process.x` access throws ReferenceError before the component
 * mounts. If new code needs a dev-only branch, call `devWarn` or
 * read `IS_PROD` from this module instead of touching `process`
 * yourself.
 */
declare const process:
  | {
      env: { NODE_ENV?: string };
    }
  | undefined;

/* Bundler-less browser caveat: a consumer that imports the ESM
 * directly from a CDN (Skypack / unpkg / import maps) has
 * `typeof process === 'undefined'`, so IS_PROD resolves to false
 * and dev warnings fire at full verbosity. Not a regression — the
 * bundler-less path is not the intended prod deployment (UMD is),
 * and consumer bundlers (Vite, webpack, Rollup, tsup) substitute
 * `process.env.NODE_ENV` and DCE the guarded branch as expected.
 */
export const IS_PROD: boolean =
  typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'production';

export function devWarn(message: string, ...rest: unknown[]): void {
  if (IS_PROD) return;
  // eslint-disable-next-line @atelierpx/jacquard/no-bare-console
  console.warn(message, ...rest);
}

/* Controlled-only primitives (Input, Textarea, Select, SearchInput)
   coerce `value ?? ''` internally so React never sees a
   value-to-undefined transition that would log its own
   "uncontrolled → controlled" warning. That's deliberate for the
   happy path, but it also silently absorbs consumer bugs (form
   state that lost its field but kept the component mounted).

   Dedupe scope: per-component-type, module-level. The first <Input>
   that receives value={undefined} warns; every subsequent Input
   mount — whether the same instance re-rendering or a fresh one —
   is silent. This prevents log spam in apps that render many
   instances from a shared root cause. If you need per-instance
   signalling, mirror SegmentedControl's warnedRef pattern inside
   the primitive itself.

   Silent in production builds via the IS_PROD gate.

   CreatableSelect is deliberately not wired here: its `value` is
   legitimately optional (the user hasn't picked anything yet), so
   a warn on undefined would be consistent false-positive noise. */
const _seenUndefinedValue = new Set<string>();
export function devWarnValueUndefined(componentName: string): void {
  if (IS_PROD) return;
  if (_seenUndefinedValue.has(componentName)) return;
  _seenUndefinedValue.add(componentName);
  // eslint-disable-next-line @atelierpx/jacquard/no-bare-console
  console.warn(
    `[JKit.${componentName}] received \`value={undefined}\`. The kit ` +
      'coerces this to "" for the controlled-only contract; this warning ' +
      "is just a dev-time signal so the consumer's state isn't silently " +
      'desynced. Pass an empty string explicitly if the field is intentionally empty.',
  );
}

/* Warn when a controlled-only primitive receives a `defaultChecked`
   / `defaultValue` prop that will be silently dropped. TS Omit
   already blocks the typed path; this covers the cast-through-
   unknown escape hatch. Module-level dedupe keyed on component +
   prop name (global spam protection, same default as
   devWarnValueUndefined). */
const _seenIgnoredDefault = new Set<string>();
export function devWarnDefaultIgnored(componentName: string, propName: string): void {
  if (IS_PROD) return;
  const key = `${componentName}.${propName}`;
  if (_seenIgnoredDefault.has(key)) return;
  _seenIgnoredDefault.add(key);
  // eslint-disable-next-line @atelierpx/jacquard/no-bare-console
  console.warn(
    `[JKit.${componentName}] received \`${propName}\` but is ` +
      'controlled-only; the prop was silently dropped. Use the ' +
      '`checked`/`value` + `onChange` pair, or drop down to a ' +
      'native <input> if you need uncontrolled semantics.',
  );
}
